import { useCallback, useEffect, useState } from "react"
import { OfflineBoiloutBundle } from "@/boilout/lib/offline-store"
import useStore from "@/store"
import {
  ensureOfflineBundleAvailable,
  getBoiloutOfflineBundleErrorMessage,
} from "@/services/boilout"
import { useInternetStatus } from "@/hooks/use-internet-status"

interface UseEnsureBoiloutOfflineBundleOptions {
  enabled?: boolean
}

interface UseEnsureBoiloutOfflineBundleResult {
  bundle: OfflineBoiloutBundle | null
  isLoading: boolean
  error: string | null
  ensureBundle: () => Promise<OfflineBoiloutBundle | null>
}

function hasHandoverEmployees(bundle: OfflineBoiloutBundle | null): boolean {
  return !!bundle && Array.isArray(bundle.employees)
}

export function useEnsureBoiloutOfflineBundle(
  options: UseEnsureBoiloutOfflineBundleOptions = {}
): UseEnsureBoiloutOfflineBundleResult {
  const { enabled = true } = options
  const bundle = useStore((state) => state.boilout.offline.bundle)
  const { isOnline } = useInternetStatus()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ensureBundle = useCallback(async () => {
    if (!enabled) {
      return bundle
    }

    if (hasHandoverEmployees(bundle)) {
      setError(null)
      return bundle
    }

    setIsLoading(true)

    try {
      const resolvedBundle = await ensureOfflineBundleAvailable({ isOnline })
      setError(null)
      return resolvedBundle
    } catch (err) {
      setError(getBoiloutOfflineBundleErrorMessage(err))
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [bundle, enabled, isOnline])

  useEffect(() => {
    if (!enabled || hasHandoverEmployees(bundle)) {
      return
    }

    void ensureBundle().catch(() => {})
  }, [bundle, enabled, ensureBundle])

  useEffect(() => {
    if (hasHandoverEmployees(bundle)) {
      setError(null)
    }
  }, [bundle])

  return {
    bundle,
    isLoading,
    error,
    ensureBundle,
  }
}
